// apps/web/src/routes/settings-widget.tsx
import { useState, useEffect } from 'react'
import useSWR from 'swr'
import { Input } from '@viecxanh/chat-ui/input'
import { Textarea } from '@viecxanh/chat-ui/textarea'
import { Button } from '@viecxanh/chat-ui/button'
import { Switch } from '@viecxanh/chat-ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@viecxanh/chat-ui/select'
import { Skeleton } from '@viecxanh/chat-ui/skeleton'
import { Separator } from '@viecxanh/chat-ui/separator'
import { api } from '../lib/api-client'
import type { WidgetConfig } from '@viecxanh/chat-shared'

export function WidgetSettingsPage() {
  const { data, isLoading, mutate } = useSWR<WidgetConfig>(
    '/api/admin/settings/widget',
    (url: string) => api.get<WidgetConfig>(url),
  )

  const [form, setForm] = useState<Partial<WidgetConfig>>({})
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await api.put('/api/admin/settings/widget', form)
      await mutate()
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  const embedCode = data?.api_key
    ? `<script src="${import.meta.env.VITE_API_BASE ?? 'http://localhost:3001'}/widget.js" data-key="${data.api_key}" async defer></script>`
    : ''

  if (isLoading) return <Skeleton className="h-64 w-full" />

  return (
    <div className="space-y-6 max-w-xl">
      <h2 className="text-lg font-semibold">Cài đặt Widget nhúng</h2>

      <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium">Tin nhắn chào</label>
          <Textarea
            value={form.welcome_message ?? ''}
            onChange={(e) => setForm({ ...form, welcome_message: e.target.value })}
            rows={2}
          />
        </div>

        <div className="flex gap-4">
          <div className="space-y-1 flex-1">
            <label className="text-sm font-medium">Màu chủ đề</label>
            <Input
              type="color"
              value={form.theme_color ?? '#22c55e'}
              onChange={(e) => setForm({ ...form, theme_color: e.target.value })}
              className="h-9 w-full"
            />
          </div>
          <div className="space-y-1 flex-1">
            <label className="text-sm font-medium">Vị trí</label>
            <Select
              value={form.position ?? 'bottom-right'}
              onValueChange={(v) => setForm({ ...form, position: v as WidgetConfig['position'] })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bottom-right">Phải dưới</SelectItem>
                <SelectItem value="bottom-left">Trái dưới</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Domains cho phép (mỗi domain một dòng)</label>
          <Textarea
            value={(form.domains_whitelist ?? []).join('\n')}
            onChange={(e) => setForm({
              ...form,
              domains_whitelist: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
            })}
            rows={3}
            placeholder={'xanhvina.com.vn\n*.xanhvina.com.vn'}
          />
        </div>

        <div className="flex items-center gap-2">
          <Switch
            checked={form.show_working_hours ?? false}
            onCheckedChange={(v) => setForm({ ...form, show_working_hours: v })}
          />
          <span className="text-sm">Hiển thị giờ làm việc cho khách</span>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            checked={form.is_active ?? true}
            onCheckedChange={(v) => setForm({ ...form, is_active: v })}
          />
          <span className="text-sm">Kích hoạt widget</span>
        </div>

        <Button type="submit" disabled={saving} className="bg-brand hover:bg-brand-dark">
          {saved ? 'Đã lưu ✓' : saving ? 'Đang lưu...' : 'Lưu thay đổi'}
        </Button>
      </form>

      {embedCode && (
        <>
          <Separator />
          <div className="space-y-2">
            <p className="font-medium">Mã nhúng</p>
            <p className="text-sm text-slate-500">Dán đoạn mã này vào thẻ &lt;body&gt; của trang web.</p>
            <div className="relative">
              <pre className="rounded-md bg-slate-900 p-4 text-xs text-green-400 overflow-x-auto whitespace-pre-wrap break-all">
                {embedCode}
              </pre>
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => void navigator.clipboard.writeText(embedCode)}
              >
                Sao chép
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
